<template>
  <div
    v-if="modelValue"
    class="combination_popup"
    @click.self="handleClose"
    @touchmove.prevent
  >
    <div class="popup_panel">
      <div class="popup_header">
        <div class="popup_title">请选择已有建沪深模拟组合进行绑定</div>
        <div class="close_btn" @click="handleClose">
          <img src="../images/close.png" alt="" />
        </div>
      </div>

      <div class="table_head" v-if="list.length">
        <div class="col_name">组合名称</div>
        <div class="col_net">净值</div>
        <div class="col_pick">选择</div>
      </div>

      <div class="list_wrap" v-if="list.length" @touchmove.stop>
        <div
          class="list_row"
          v-for="item in list"
          :key="item.id"
          @click="handlePick(item.id)"
        >
          <div class="col_name">{{ item.name }}</div>
          <div class="col_net">
            {{ item.nav && item.nav.toFixed(3) }}
          </div>
          <div class="col_pick">
            <span
              class="radio"
              :class="{ is_checked: selectedId === item.id }"
            ></span>
          </div>
        </div>
      </div>

      <div v-else class="empty_wrap">
        <div class="empty_icon">
          <img src="../images/no_data.png" alt="暂无组合" />
        </div>
        <div class="empty_text">您还没有创建组合</div>
      </div>

      <div
        class="confirm_btn"
        @click="list.length ? handleConfirm() : handleCreate()"
        :tracker-eventcode="list.length ?  'zhdcqd_000' : 'zhdcqcj_000'"
      >
        {{ list.length ? "确定" : "去创建" }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, ref, watch } from "vue";
import { $toast } from "@/utils/globalUtils";
import { appInfo } from "@eastmoney/mku";

export default defineComponent({
  name: "CombinationSelectPopup",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    defaultSelectedId: {
      type: String,
      default: "",
    },
    list: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:modelValue", "confirm"],
  setup(props, { emit }) {
    const selectedId = ref("");
    let bodyOriginalOverflow = "";
    let bodyOriginalPosition = "";
    let bodyOriginalTop = "";
    let bodyOriginalWidth = "";
    let pageScrollTop = 0;

    const lockBodyScroll = () => {
      pageScrollTop =
        window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      bodyOriginalOverflow = document.body.style.overflow;
      bodyOriginalPosition = document.body.style.position;
      bodyOriginalTop = document.body.style.top;
      bodyOriginalWidth = document.body.style.width;

      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${pageScrollTop}px`;
      document.body.style.width = "100%";
    };

    const unlockBodyScroll = () => {
      document.body.style.overflow = bodyOriginalOverflow;
      document.body.style.position = bodyOriginalPosition;
      document.body.style.top = bodyOriginalTop;
      document.body.style.width = bodyOriginalWidth;
      window.scrollTo(0, pageScrollTop);
    };

    watch(
      () => props.modelValue,
      (visible) => {
        if (visible) {
          lockBodyScroll();
        } else {
          unlockBodyScroll();
        }

        if (visible) {
          const targetId = props.defaultSelectedId;
          if (!targetId || !props.list?.length) {
            selectedId.value = "";
            return;
          }
          const matchedItem: any = props.list.find(
            (item: any) => item?.id === targetId
          );
          selectedId.value = matchedItem ? matchedItem.id : "";
        }
      },
      { immediate: true }
    );

    onBeforeUnmount(() => {
      unlockBodyScroll();
    });

    const handlePick = (id: string) => {
      selectedId.value = id;
    };

    const handleClose = () => {
      emit("update:modelValue", false);
    };

    const handleConfirm = () => {
      if (!selectedId.value) {
        $toast.show("请选择一个组合进行绑定");
        return;
      }

      const selectedItem =
        props.list.find((item: any) => item.id === selectedId.value) || null;
      emit("confirm", selectedItem);
    };

    const handleCreate = () => {
      if (appInfo.isIOS) {
        window.location.href = `eastmoney://page/createPortfolio?portType=0&marketType=0`;
      } else {
        window.location.href = `dfcft://router/portfolio/createPfDetail?pfType=0&marketType=1`;
      }
      emit("update:modelValue", false);
    };

    return {
      selectedId,
      handlePick,
      handleClose,
      handleConfirm,
      handleCreate,
    };
  },
});
</script>

<style scoped lang="scss">
.combination_popup {
  position: fixed;
  inset: 0;
  z-index: 30;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-end;

  .popup_panel {
    width: 100%;
    min-height: 6.2rem;
    max-height: calc(100vh - 5.6rem);
    border-radius: 0.24rem 0.24rem 0 0;
    background: #fff;
    overflow: hidden;
    display: flex;
    box-sizing: border-box;
    flex-direction: column;
    padding: 0 0.32rem;

    padding-bottom: 0.3rem;

    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }
  }

  .popup_header {
    height: 0.9rem;
    display: flex;
    align-items: center;

    padding-top: 0.32rem;
    .popup_title {
      flex: 1;
      font-size: 0.36rem;
      color: #333;
      font-weight: 600;
    }

    .close_btn {
      width: 0.48rem;
      height: 0.48rem;
      font-size: 0;
      img {
        width: 100%;
        height: 100%;
      }
    }
  }

  .table_head {
    margin-top: 0.2rem;
    margin-bottom: 0.1rem;
    display: flex;
    height: 0.64rem;
    align-items: center;
    font-size: 0.24rem;
    color: #999;

    .col_name {
      flex: 1;
    }

    .col_net {
      width: 1.5rem;
      text-align: center;
    }

    .col_pick {
      box-sizing: border-box;
      padding-right: 0.1rem;
      width: 1.5rem;
      display: flex;
      justify-content: flex-end;
    }
  }

  .list_wrap {
    flex: 1;
    min-height: 2.7rem;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;

    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }
  }

  .empty_wrap {
    flex: 1;
    min-height: 2.7rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .empty_icon {
      width: 3.36rem;
      height: 1.86rem;
      font-size: 0;
      img {
        width: 100%;
        height: 100%;
      }
    }

    .empty_text {
      margin-top: 0.24rem;
      font-size: 0.3rem;
      color: #9b9b9b;
    }
  }

  .list_row {
    display: flex;
    align-items: center;
    height: 0.9rem;
    font-size: 0.3rem;
    color: #333;
    border-bottom: 0.02rem solid #f5f5f5;

    .col_name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .col_net {
      width: 1.5rem;
      text-align: center;

      &.is_up {
        color: #00a120;
      }
    }

    .col_pick {
      box-sizing: border-box;
      padding-right: 0.1rem;
      width: 1.5rem;
      display: flex;
      justify-content: flex-end;
    }

    .radio {
      width: 0.34rem;
      height: 0.34rem;
      border-radius: 50%;
      border: 0.02rem solid #bfbfbf;
      box-sizing: border-box;
      position: relative;

      &.is_checked {
        border-color: #ff5500;
        background: #ff5500;

        &::after {
          content: "";
          position: absolute;
          left: 0.1rem;
          top: 0.06rem;
          width: 0.08rem;
          height: 0.14rem;
          border: 0.03rem solid #fff;
          border-top: none;
          border-left: none;
          transform: rotate(45deg);
        }
      }
    }
  }

  .confirm_btn {
    flex-shrink: 0;
    width: 7rem;
    height: 0.92rem;
    margin: 0.22rem auto 0;
    margin-top: 0.4rem;
    border-radius: 0.46rem;
    background: #ff5500;
    color: #fff;
    font-size: 0.34rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
